import { useState, useEffect, useMemo } from 'react';
import api, { listRentPayments, upsertRentPayment, regenerateRentPayments } from '../services/api';
import { CheckCircle, Warning, Clock, CurrencyEur, ArrowsClockwise, X, FloppyDisk } from '@phosphor-icons/react';

const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function eur(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

const STATUS_META = {
  paid:    { label: 'bezahlt',   tone: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle },
  open:    { label: 'offen',     tone: 'bg-slate-100 text-slate-700 border-slate-200',       icon: Clock },
  overdue: { label: 'überfällig',tone: 'bg-red-100 text-red-800 border-red-200',             icon: Warning },
  partial: { label: 'teilweise', tone: 'bg-amber-100 text-amber-800 border-amber-200',       icon: Clock },
};

export default function Mieteinnahmen() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [tenants, setTenants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {tenant, year, month, payment}

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        api.get('/tenants'),
        listRentPayments({ year }),
      ]);
      setTenants(tRes.data || []);
      setPayments(pRes.data?.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year]);

  // index: payments by tenant_id + month
  const paymentMap = useMemo(() => {
    const m = {};
    for (const p of payments) {
      m[`${p.tenant_id}-${p.month}`] = p;
    }
    return m;
  }, [payments]);

  const totals = useMemo(() => {
    let soll = 0, ist = 0;
    for (const p of payments) {
      soll += p.soll_gesamt || 0;
      ist += p.ist_betrag || 0;
    }
    return { soll, ist, diff: ist - soll };
  }, [payments]);

  const regenerate = async (tenantId) => {
    try {
      await regenerateRentPayments(tenantId);
      await load();
    } catch (e) { console.error(e); }
  };

  // Only show tenants who have either rent configured OR existing payments
  const visibleTenants = useMemo(() => {
    const idsWithPay = new Set(payments.map(p => p.tenant_id));
    return tenants.filter(t => idsWithPay.has(t.id) || (t.miete_gesamt && t.miete_gesamt > 0));
  }, [tenants, payments]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mieteinnahmen-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
            Mieteinnahmen
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Mietkonto pro Mieter · Soll vs. Ist · monatliche Übersicht</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Jahr</label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="border border-slate-200 px-3 py-2 rounded text-sm"
            data-testid="year-select"
          >
            {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryTile label="Soll-Miete" value={eur(totals.soll)} tone="bg-slate-50 border-slate-200 text-slate-700" />
        <SummaryTile label="Erhalten" value={eur(totals.ist)} tone="bg-emerald-50 border-emerald-200 text-emerald-700" />
        <SummaryTile
          label="Differenz"
          value={eur(totals.diff)}
          tone={totals.diff < 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'}
        />
      </div>

      {/* Matrix */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-3 py-2 font-medium text-slate-700 sticky left-0 bg-slate-50">Mieter</th>
                <th className="text-right px-3 py-2 font-medium text-slate-700">Soll/Monat</th>
                {MONTHS.map((m, i) => (
                  <th key={i} className="text-center px-2 py-2 font-medium text-slate-700">{m}</th>
                ))}
                <th className="text-right px-3 py-2 font-medium text-slate-700">Summe</th>
                <th className="text-center px-2 py-2 font-medium text-slate-700">⟲</th>
              </tr>
            </thead>
            <tbody>
              {visibleTenants.length === 0 ? (
                <tr><td colSpan={16} className="px-4 py-8 text-center text-slate-400">
                  Keine Mieter mit konfigurierter Miete. Setze Kaltmiete + NK-VZ im Mieter-Detail, dann werden automatisch monatliche Einträge angelegt.
                </td></tr>
              ) : visibleTenants.map(tenant => {
                let rowIst = 0;
                let rowSoll = 0;
                return (
                  <tr key={tenant.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-${tenant.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-900 sticky left-0 bg-white whitespace-nowrap">{tenant.name}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-700">
                      {tenant.miete_gesamt ? eur(tenant.miete_gesamt) : '—'}
                    </td>
                    {MONTHS.map((_, mi) => {
                      const month = mi + 1;
                      const p = paymentMap[`${tenant.id}-${month}`];
                      if (!p) {
                        return <td key={mi} className="px-1 py-1 text-center text-slate-300">·</td>;
                      }
                      rowIst += p.ist_betrag || 0;
                      rowSoll += p.soll_gesamt || 0;
                      const meta = STATUS_META[p.status] || STATUS_META.open;
                      return (
                        <td key={mi} className="px-1 py-1 text-center" data-testid={`cell-${tenant.id}-${month}`}>
                          <button
                            type="button"
                            onClick={() => setEditing({ tenant, year, month, payment: p })}
                            className={`inline-flex items-center justify-center text-[10px] uppercase tracking-wide font-bold border ${meta.tone} hover:opacity-80 px-2 py-1 rounded`}
                            title={`Soll ${eur(p.soll_gesamt)} / Ist ${eur(p.ist_betrag)} - ${meta.label}`}
                          >
                            {meta.label}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-mono">
                      <span className={rowIst < rowSoll ? 'text-amber-700' : 'text-emerald-700'}>
                        {eur(rowIst)}
                      </span>
                      <span className="text-slate-400 text-xs"> / {eur(rowSoll)}</span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => regenerate(tenant.id)}
                        title="Fehlende Monate automatisch anlegen"
                        className="text-slate-400 hover:text-slate-900"
                      >
                        <ArrowsClockwise weight="bold" className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-600">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 border ${m.tone} rounded`} /> {m.label}
          </span>
        ))}
      </div>

      {editing && (
        <EditPaymentModal
          {...editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, tone }) {
  return (
    <div className={`border p-5 rounded ${tone}`}>
      <p className="text-xs uppercase tracking-widest font-bold opacity-75">{label}</p>
      <p className="font-mono text-3xl font-black mt-2">{value}</p>
    </div>
  );
}

function EditPaymentModal({ tenant, year, month, payment, onClose, onSaved }) {
  const [istBetrag, setIstBetrag] = useState(payment?.ist_betrag ?? 0);
  const [istDatum, setIstDatum] = useState(payment?.ist_datum || new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState(payment?.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const val = parseFloat(String(istBetrag).replace(',', '.')) || 0;
      await upsertRentPayment({
        tenant_id: tenant.id,
        year, month,
        ist_betrag: val,
        ist_datum: val > 0 ? istDatum : null,
        notes: notes || null,
      });
      onSaved();
    } catch (e) {
      console.error(e);
    } finally { setSaving(false); }
  };

  const markPaid = async () => {
    setSaving(true);
    try {
      await upsertRentPayment({
        tenant_id: tenant.id,
        year, month,
        ist_betrag: payment.soll_gesamt,
        ist_datum: new Date().toISOString().slice(0, 10),
      });
      onSaved();
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="payment-modal">
      <div className="bg-white max-w-md w-full rounded-lg shadow-xl">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900">{tenant.name}</h2>
            <p className="text-xs text-slate-500">{MONTHS[month-1]} {year}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X weight="bold" className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="bg-slate-50 border border-slate-100 p-2 rounded">
              <p className="text-[10px] uppercase font-bold text-slate-500">Kalt</p>
              <p className="font-mono">{eur(payment.soll_kalt)}</p>
            </div>
            <div className="bg-slate-50 border border-slate-100 p-2 rounded">
              <p className="text-[10px] uppercase font-bold text-slate-500">NK-VZ</p>
              <p className="font-mono">{eur(payment.soll_nk)}</p>
            </div>
            <div className="bg-slate-50 border border-slate-100 p-2 rounded">
              <p className="text-[10px] uppercase font-bold text-slate-500">Heizung</p>
              <p className="font-mono">{eur(payment.soll_heizung)}</p>
            </div>
          </div>
          <div className="bg-slate-900 text-white p-3 flex items-center justify-between rounded">
            <span className="text-sm">Soll-Miete</span>
            <span className="font-mono font-bold text-lg">{eur(payment.soll_gesamt)}</span>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Erhalten (Ist) €</label>
            <input
              type="text"
              inputMode="decimal"
              value={istBetrag}
              onChange={(e) => setIstBetrag(e.target.value)}
              className="w-full border border-slate-200 px-3 py-2 font-mono rounded focus:border-slate-900 outline-none"
              data-testid="ist-betrag-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Zahlungsdatum</label>
            <input
              type="date"
              value={istDatum}
              onChange={(e) => setIstDatum(e.target.value)}
              className="w-full border border-slate-200 px-3 py-2 rounded focus:border-slate-900 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Notiz</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-slate-200 px-3 py-2 rounded focus:border-slate-900 outline-none text-sm"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex gap-2 justify-between">
          <button
            onClick={markPaid}
            disabled={saving}
            className="text-sm text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
            data-testid="mark-paid-button"
          >
            <CheckCircle weight="bold" className="inline w-4 h-4 mr-1" />
            Als voll bezahlt markieren
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="border border-slate-200 px-4 py-1.5 text-sm rounded">Abbrechen</button>
            <button
              onClick={save}
              disabled={saving}
              className="bg-slate-900 text-white px-4 py-1.5 text-sm font-medium rounded disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="save-payment-button"
            >
              <FloppyDisk weight="bold" className="w-4 h-4" />
              Speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
